/**
 * remark plugin: parses `{table:option,option}` directive lines that
 * immediately precede a GFM table and attaches the parsed options to
 * the following table node as CSS classes.
 *
 * GFM doesn't support whole-table styling (alignment, "header column"
 * patterns, etc.) — only per-column horizontal alignment in the
 * separator row. Rather than allowing raw HTML wrappers (which would
 * mean enabling `rehype-raw` and inviting XSS), this plugin defines a
 * narrow, controlled directive syntax that the table builder dialog
 * emits and reads back when editing existing tables.
 *
 * Recognized options:
 *   - `center`            — center the whole table within the body
 *   - `primary=0|2`       — column indices to style with the same
 *                           chrome as the header row (pipe-separated
 *                           so the directive line can survive embedded
 *                           commas in other options without ambiguity)
 *
 * Example source:
 *   {table:center,primary=0}
 *   | metric    | 2024 | 2025 |
 *   | ---       | ---  | ---  |
 *   | Press PPP | 110  | 113  |
 *
 * Rendered: the table is horizontally centered and the first body
 * column gets the same gray-fill + bold styling as the header row.
 * Styling itself lives in `ArticleBody.tsx` — this plugin only tags.
 *
 * Helpers `parseTableDirective` and `formatTableDirective` are exported
 * so `tableModel.ts` can read/write the directive line during
 * serialization roundtrips.
 */

import type { Node, Parent } from "unist";

interface TextNode extends Node {
  type: "text";
  value: string;
}

const DIRECTIVE_PATTERN = /^\s*\{table:([^}]+)\}\s*$/;

export interface ParsedTableDirective {
  center?: boolean;
  primaryCols?: number[];
}

export function parseTableDirective(line: string): ParsedTableDirective | null {
  const m = line.match(DIRECTIVE_PATTERN);
  if (!m) return null;
  const opts: ParsedTableDirective = {};
  for (const raw of m[1].split(",").map((s) => s.trim())) {
    if (raw === "center") {
      opts.center = true;
    } else if (raw.startsWith("primary=")) {
      const cols = raw
        .slice("primary=".length)
        .split(/[|\s]+/)
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isInteger(n) && n >= 0);
      if (cols.length) opts.primaryCols = cols;
    }
  }
  return Object.keys(opts).length === 0 ? null : opts;
}

/**
 * Reverse of `parseTableDirective`. Returns `null` if no options are
 * set, so the serializer can skip emitting the directive line when
 * everything is default.
 */
export function formatTableDirective(
  opts: ParsedTableDirective,
): string | null {
  const parts: string[] = [];
  if (opts.center) parts.push("center");
  if (opts.primaryCols && opts.primaryCols.length > 0) {
    parts.push("primary=" + opts.primaryCols.join("|"));
  }
  if (parts.length === 0) return null;
  return `{table:${parts.join(",")}}`;
}

const remarkTableDirectives = () => {
  return (tree: Node) => {
    const root = tree as Parent;
    if (!Array.isArray(root.children)) return;

    const toRemove: number[] = [];

    for (let i = 0; i < root.children.length; i++) {
      const node = root.children[i];
      // Directives parse as paragraphs containing a single text child.
      if (node.type !== "paragraph") continue;
      const para = node as Parent;
      if (para.children.length !== 1) continue;
      const child = para.children[0];
      if (child.type !== "text") continue;
      const directive = parseTableDirective((child as TextNode).value);
      if (!directive) continue;

      // Skip any empty paragraphs between the directive and the table
      // (the GFM parser sometimes inserts a blank paragraph if the
      // author left a blank line between them in the source).
      let nextIdx = i + 1;
      while (nextIdx < root.children.length) {
        const next = root.children[nextIdx];
        const nextAsParent = next as Parent;
        if (
          next.type === "paragraph" &&
          (!nextAsParent.children || nextAsParent.children.length === 0)
        ) {
          nextIdx++;
          continue;
        }
        break;
      }

      const tableNode = root.children[nextIdx];
      if (!tableNode || tableNode.type !== "table") continue;

      // Apply table-level classes via `data.hProperties`.
      const tNode = tableNode as any;
      tNode.data = tNode.data ?? {};
      tNode.data.hProperties = tNode.data.hProperties ?? {};
      const tableClasses = ensureClassArray(tNode.data.hProperties.className);
      if (directive.center) tableClasses.push("table-center");
      if (tableClasses.length) tNode.data.hProperties.className = tableClasses;

      // Apply per-cell classes for primary columns by walking the
      // table's rows. Tagging at the mdast level (rather than via a
      // component prop walk at render time) keeps the rendering
      // override on `<td>` simple — just read `className`.
      if (directive.primaryCols && directive.primaryCols.length > 0) {
        const primarySet = new Set(directive.primaryCols);
        const tableParent = tableNode as Parent;
        for (const row of tableParent.children) {
          if ((row as any).type !== "tableRow") continue;
          const rowParent = row as Parent;
          for (let ci = 0; ci < rowParent.children.length; ci++) {
            if (!primarySet.has(ci)) continue;
            const cell = rowParent.children[ci] as any;
            cell.data = cell.data ?? {};
            cell.data.hProperties = cell.data.hProperties ?? {};
            const cellClasses = ensureClassArray(
              cell.data.hProperties.className,
            );
            cellClasses.push("primary-col");
            cell.data.hProperties.className = cellClasses;
          }
        }
      }

      toRemove.push(i);
    }

    // Remove directive paragraphs in descending order so earlier
    // indices stay valid as we splice.
    for (let k = toRemove.length - 1; k >= 0; k--) {
      root.children.splice(toRemove[k], 1);
    }
  };
};

function ensureClassArray(existing: unknown): string[] {
  if (!existing) return [];
  if (Array.isArray(existing)) return existing.map(String);
  return [String(existing)];
}

export default remarkTableDirectives;
